import { Checkmark, Calculation } from '@carbon/react/icons';
import type {
  TractNriMatrixRow,
  TractNriMatrixBreakdownItem,
} from 'types/api-responses';
import styles from './tract-nri-matrix-table.module.scss';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { MatrixTable } from 'components/matrix-table';
import { BaseCard } from 'components/base-card';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { CalculationType } from 'components/nri-table/types';
import { TractNriBreakdownPopup } from 'pages/crm-tracts-subtracts/nri/tract-nri-breakdown-popup';
import { formatAllocationMethod } from 'pages/crm-tracts-subtracts/nri/tract-nri-matrix-utils';

interface TractNriMatrixTableProps {
  rows: TractNriMatrixRow[];
  breakdown: TractNriMatrixBreakdownItem[];
  calculationType?: CalculationType;
  wellName?: string;
}

const getHeaders = (calculationType: CalculationType) => [
  { key: 'inUse', header: 'In Use' },
  { key: 'allocationMethod', header: 'Allocation Method' },
  {
    key: 'slotInterest',
    header: calculationType === 'WI' ? 'Total Slot WI' : 'Total Slot NRI',
  },
];

interface FormattedRow extends TractNriMatrixRow {
  id: string;
  allocationMethodDisplay: string;
  slotInterestDisplay: React.ReactNode;
}

export const TractNriMatrixTable = ({
  rows: originalRows,
  breakdown,
  calculationType = 'NRI',
  wellName,
}: TractNriMatrixTableProps) => {
  const headers = getHeaders(calculationType);

  const tableRows: FormattedRow[] = originalRows.map((row, index) => {
    const interestValue =
      calculationType === 'WI' ? row.aggregatedWi : row.aggregatedNri;
    const formattedRow: FormattedRow = {
      id: String(index),
      ...row,
      allocationMethodDisplay: formatAllocationMethod(
        row.allocationMethod,
        row.source
      ),
      slotInterestDisplay:
        interestValue !== null
          ? formatNumberWithTooltip(interestValue, 8)
          : '--',
    };
    return formattedRow;
  });

  const renderSlotInterestCell = (
    row: TractNriMatrixRow,
    formattedRow: FormattedRow
  ) => {
    const interestValue =
      calculationType === 'WI' ? row.aggregatedWi : row.aggregatedNri;

    if (interestValue === null) {
      return <span>--</span>;
    }

    // Filter breakdown items to get rows matching this allocation method and source
    const filteredBreakdown = breakdown.map((item) => ({
      ...item,
      matchingRow: item.rows.find(
        (r) =>
          r.allocationMethod === row.allocationMethod && r.source === row.source
      ),
    }));

    const renderTrigger = (active: boolean, toggle: () => void) => (
      <CalculationIconTrigger
        definition="Show Breakdown"
        active={active}
        onClick={toggle}
      >
        <div className={styles.iconContainer}>
          <Calculation aria-hidden="true" role="img" />
        </div>
      </CalculationIconTrigger>
    );

    return (
      <div className={styles.slotInterestCell}>
        <span>{formattedRow.slotInterestDisplay}</span>
        <BaseCard
          contentClassName={styles.breakdownCardContent}
          renderTrigger={renderTrigger}
          content={
            <TractNriBreakdownPopup
              breakdownItems={filteredBreakdown}
              allocationMethod={row.allocationMethod}
              source={row.source}
              calculationType={calculationType}
              wellName={wellName}
            />
          }
        />
      </div>
    );
  };

  /**
   * One matrix cell's content.
   *
   * @param {string} columnKey - The cell's column.
   * @param {number} rowIndex - The cell's row.
   * @return {React.ReactNode} The content.
   */
  const renderCell = (columnKey: string, rowIndex: number) => {
    const rowData = originalRows[rowIndex];
    const formattedRow = tableRows[rowIndex];
    if (!rowData || !formattedRow) return null;

    if (columnKey === 'inUse') return rowData.inUse ? <Checkmark /> : null;
    if (columnKey === 'allocationMethod') {
      return formattedRow.allocationMethodDisplay;
    }
    return renderSlotInterestCell(rowData, formattedRow);
  };

  return (
    <MatrixTable
      className={styles.tableWrapper}
      rows={tableRows}
      columns={headers}
      renderCell={renderCell}
    />
  );
};
